/**
 * Impostazioni, sezione «Come lavora Otto»: il quaderno delle risposte.
 */

import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  House,
  NotebookPen,
  RefreshCw,
  X,
} from "lucide-react";
import {
  api,
  nomeCasa,
  perDomanda,
  type GiroQuaderno,
  type RiscontroView,
  type SchedaView,
} from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { Card, Pill, cx } from "../../ui/primitives.tsx";

/** Quante schede da decidere si vedono prima di «Mostra tutte». */
const SCHEDE_IN_VISTA = 3;

/** Quando rilegge i messaggi: «ogni quanto» scritto dove si guardano le schede. */
function RigaGiro({ giro }: { giro: GiroQuaderno }) {
  const data = (iso: string) =>
    new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
    });
  const testo = !giro.automatico
    ? "Il giro automatico è spento: manca la chiave del modello."
    : giro.prossimoDal == null
      ? "Rileggo i messaggi una volta a settimana. Il primo giro parte appena ci sono abbastanza domande degli ospiti."
      : `Rileggo i messaggi una volta a settimana${
          giro.ultimo ? `: l'ultima volta il ${data(giro.ultimo)}` : ""
        }. Il prossimo giro dal ${data(giro.prossimoDal)}, se sono arrivati messaggi nuovi.`;
  return (
    <p className="mt-3 flex items-start gap-2 rounded-field bg-surface-muted px-3 py-2 text-[12.5px] leading-relaxed text-ink-soft">
      <RefreshCw size={13} className="mt-0.5 shrink-0 text-ink-faint" />
      {testo}
    </p>
  );
}

/**
 * Per quale casa vale una scheda. Sempre detto, anche «Tutte le case»: il
 * silenzio non distingue «vale ovunque» da «non so di quale casa è».
 */
function CasaDellaScheda({ scheda }: { scheda: SchedaView }) {
  return (
    <p className="mt-0.5 flex items-center gap-1 text-[12px] text-ink-faint">
      <House size={12} className="shrink-0" />
      {nomeCasa(scheda)}
    </p>
  );
}

/**
 * Il quaderno: come Otto risponderebbe alle domande che tornano.
 *
 * Una scheda per volta, e due uscite sole — «Va bene» o «Correggi». Non c'è un
 * «forse»: una scheda a cui l'host non risponde resta proposta e non entra in
 * niente, che è il no più onesto che si possa dare a chi non ha tempo.
 *
 * Sotto ogni scheda ci sono **le frasi vere da cui è uscita**, con la data. È
 * il pezzo che rende la domanda rispondibile: senza, all'host si sta chiedendo
 * di fidarsi di un testo che gli compare davanti, e di quello non ci si fida —
 * giustamente.
 */
export function Quaderno() {
  const [schede, setSchede] = useState<SchedaView[] | null>(null);
  const [riscontro, setRiscontro] = useState<RiscontroView | null>(null);
  const [giro, setGiro] = useState<GiroQuaderno | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorrezione, setInCorrezione] = useState<string | null>(null);
  const [bozza, setBozza] = useState("");

  const carica = () =>
    api
      .quaderno()
      .then((r) => {
        setSchede(r.schede);
        setRiscontro(r.riscontro ?? null);
        setGiro(r.giro ?? null);
      })
      .catch((e: unknown) => setErrore(messaggioDi(e)));

  useEffect(() => {
    void carica();
  }, []);

  const decidi = async (
    scheda: SchedaView,
    stato: "attiva" | "scartata",
    correzione?: string,
  ) => {
    setInCorrezione(null);
    // Ottimistico: la decisione è reversibile e il giro di rete si sente.
    setSchede((prima) =>
      (prima ?? []).map((s) =>
        s.id === scheda.id
          ? { ...s, stato, correzione: correzione ?? s.correzione }
          : s,
      ),
    );
    try {
      await api.decidiScheda(scheda.id, stato, correzione);
    } catch (e: unknown) {
      setErrore(messaggioDi(e));
      void carica();
    }
  };

  // Le versioni per casa della stessa domanda stanno insieme.
  const daDecidere = perDomanda(
    (schede ?? []).filter((s) => s.stato === "proposta"),
  );
  const attive = perDomanda((schede ?? []).filter((s) => s.stato === "attiva"));
  const conVersioneNuova = attive.filter((s) => !!s.aggiornamento);

  const scegliVersione = async (scheda: SchedaView, tieniNuova: boolean) => {
    try {
      await api.decidiAggiornamento(scheda.id, tieniNuova);
    } catch (e: unknown) {
      setErrore(messaggioDi(e));
    }
    void carica();
  };
  // Tre alla volta: tutte aperte allungavano la pagina fino a non trovare
  // più il resto. Le altre si aprono con «Mostra tutte».
  const [espanse, setEspanse] = useState(false);
  const inVista = espanse ? daDecidere : daDecidere.slice(0, SCHEDE_IN_VISTA);
  const nascoste = daDecidere.length - inVista.length;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <NotebookPen size={15} className="text-ink-faint" />
        <p className="text-[15px] font-bold text-ink">
          Come rispondo agli ospiti
        </p>
      </div>
      <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
        Ho guardato come hai risposto tu alle domande che tornano più spesso.
        Dimmi se ho capito bene: uso solo quelle che confermi.
      </p>
      {giro ? <RigaGiro giro={giro} /> : null}

      {errore ? (
        <p className="mt-3 text-[13px] text-danger">{errore}</p>
      ) : schede == null ? (
        <p className="mt-3 text-[13px] text-ink-faint">Guardo…</p>
      ) : schede.length === 0 ? (
        <p className="mt-3 text-[13px] text-ink-faint">
          Non ho ancora abbastanza conversazioni per dire come rispondi di
          solito. Serve che qualche domanda torni più di una volta.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {inVista.map((scheda) => (
            <div
              key={scheda.id}
              className="rounded-field border border-line p-3"
            >
              <p className="text-[13.5px] font-semibold text-ink">
                {scheda.gruppo}
                <span className="ml-1.5 font-normal text-ink-faint">
                  · {scheda.suQuante}{" "}
                  {scheda.suQuante === 1
                    ? "volta che hai risposto"
                    : "volte che hai risposto"}
                  {scheda.lingua === "it" ? "" : ` · ${scheda.lingua}`}
                </span>
              </p>
              <CasaDellaScheda scheda={scheda} />

              {inCorrezione === scheda.id ? (
                <>
                  <textarea
                    value={bozza}
                    onChange={(e) => setBozza(e.target.value)}
                    rows={5}
                    className="mt-2 w-full rounded-field border border-line bg-surface p-2 text-[13.5px] leading-relaxed text-ink"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Pill
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        void decidi(scheda, "attiva", bozza.trim())
                      }
                    >
                      Salva così
                    </Pill>
                    <Pill size="sm" onClick={() => setInCorrezione(null)}>
                      Lascia stare
                    </Pill>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-2 whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink">
                    {scheda.testo}
                  </p>
                  {scheda.nota ? (
                    <p className="mt-1.5 text-[12px] leading-snug text-ink-faint">
                      {scheda.nota}
                    </p>
                  ) : null}

                  {/* Da dove viene. Senza, è un testo che compare e basta. */}
                  {scheda.citazioni.length > 0 ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[12px] text-ink-faint">
                        Da {scheda.citazioni.length}{" "}
                        {scheda.citazioni.length === 1
                          ? "tua risposta"
                          : "tue risposte"}
                      </summary>
                      <ul className="mt-1.5 flex flex-col gap-1.5">
                        {scheda.citazioni.map((c) => (
                          <li
                            key={c.at}
                            className="border-l-2 border-line pl-2 text-[12px] leading-snug text-ink-soft"
                          >
                            {c.testo}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}

                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <Pill
                      size="sm"
                      variant="primary"
                      onClick={() => void decidi(scheda, "attiva")}
                    >
                      Va bene
                    </Pill>
                    <Pill
                      size="sm"
                      onClick={() => {
                        setInCorrezione(scheda.id);
                        setBozza(scheda.testo);
                      }}
                    >
                      Correggi
                    </Pill>
                    <Pill
                      size="sm"
                      onClick={() => void decidi(scheda, "scartata")}
                    >
                      Non la uso
                    </Pill>
                  </div>
                </>
              )}
            </div>
          ))}

          {nascoste > 0 || espanse ? (
            <button
              type="button"
              onClick={() => setEspanse((e) => !e)}
              className="flex items-center justify-center gap-1.5 rounded-field border border-dashed border-line-strong py-2.5 text-[13px] font-semibold text-ink-soft transition-colors duration-quick hover:border-ink hover:text-ink"
            >
              {espanse
                ? "Mostra meno"
                : `Mostra tutte · altre ${nascoste} da guardare`}
              <ChevronDown
                size={15}
                className={cx(
                  "transition-transform duration-quick",
                  espanse && "rotate-180",
                )}
              />
            </button>
          ) : null}

          {daDecidere.length === 0 ? (
            <p className="text-[13px] text-ink-faint">
              Non c'è altro da guardare.
            </p>
          ) : null}

          {conVersioneNuova.length > 0 ? (
            <div className="flex flex-col gap-2 pt-1">
              <p className="text-[12px] font-semibold text-ink-faint">
                Versioni nuove da scegliere ({conVersioneNuova.length})
              </p>
              {conVersioneNuova.map((scheda) => (
                <div
                  key={`${scheda.id}:agg`}
                  className="rounded-field border border-ok/30 bg-ok-soft/40 p-3"
                >
                  <p className="text-[13.5px] font-semibold text-ink">
                    {scheda.gruppo}
                  </p>
                  <CasaDellaScheda scheda={scheda} />
                  <p className="mt-1 text-[12.5px] leading-snug text-ink-faint line-through">
                    {scheda.correzione ?? scheda.testo}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-[13px] leading-snug text-ink">
                    {scheda.aggiornamento}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Pill
                      size="sm"
                      variant="primary"
                      onClick={() => void scegliVersione(scheda, true)}
                    >
                      Usa la nuova
                    </Pill>
                    <Pill
                      size="sm"
                      onClick={() => void scegliVersione(scheda, false)}
                    >
                      Tieni quella di prima
                    </Pill>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {attive.length > 0 ? (
            <div className="pt-1">
              <p className="text-[12px] font-semibold text-ink-faint">
                Confermate ({attive.length})
              </p>
              <ul className="mt-1.5 flex flex-col divide-y divide-line">
                {attive.map((scheda) => (
                  <li
                    key={scheda.id}
                    className="flex items-start gap-2 py-1.5 first:pt-0"
                  >
                    <Check
                      size={13}
                      className="mt-0.5 shrink-0 text-ink-faint"
                    />
                    <span className="min-w-0 flex-1 text-[13px] leading-snug text-ink-soft">
                      {scheda.gruppo}
                      {scheda.lingua === "it" ? "" : ` · ${scheda.lingua}`}
                      <span className="text-ink-faint">
                        {" "}
                        · {nomeCasa(scheda)}
                      </span>
                      {scheda.correzione ? (
                        <span className="ml-1 text-ink-faint">
                          (l'hai riscritta)
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      aria-label={`Non usare più: ${scheda.gruppo} · ${nomeCasa(scheda)}`}
                      onClick={() => void decidi(scheda, "scartata")}
                      className="grid size-6 shrink-0 place-items-center rounded-field text-ink-faint transition-colors duration-quick hover:bg-surface-muted hover:text-danger"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}

      {/* Fuori dall'elenco: le bozze preparate esistono anche quando il
          quaderno è vuoto, e far sparire il conto con le schede era un modo di
          nascondere proprio il caso che conta — copertura zero. */}
      {riscontro && riscontro.preparate > 0 ? (
        <p className="mt-3 border-t border-line pt-2.5 text-[12px] leading-relaxed text-ink-faint">
          {/* I due numeri stanno insieme apposta: l'accettazione da sola si
              alza stando zitti, e sarebbe il prodotto opposto. */}
          Ho preparato {riscontro.preparate}{" "}
          {riscontro.preparate === 1 ? "risposta" : "risposte"}:{" "}
          {riscontro.conScheda} partendo da una scheda
          {riscontro.copertura != null
            ? ` (${Math.round(riscontro.copertura * 100)}%)`
            : ""}
          .{" "}
          {riscontro.accettazione != null
            ? `Di quelle, ne hai approvate ${Math.round(riscontro.accettazione * 100)}%.`
            : "Troppo poche ancora per dire quante ne approvi."}
        </p>
      ) : null}
    </Card>
  );
}
